import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { setDefaultOptions } from "date-fns";
import { enUS, id as idLocale, ms as msLocale, hi as hiLocale } from "date-fns/locale";

// Bahasa console — sama dengan landing page sewoork.com (languages/<kode>.json di theme WordPress)
// dan mengikuti pola comity-apps (src/i18n).
//
// Menambah bahasa baru:
//   1. Salin folder locales/en/ menjadi locales/<kode>/ lalu terjemahkan isinya
//   2. Daftarkan di SUPPORTED_LANGUAGES di bawah (+ locale date-fns-nya)
//   3. Tambahkan kode yang sama di UsersModel::SUPPORTED_LANGUAGES (API) dan sw_languages() (landing)
// Menambah namespace baru cukup buat file locales/<kode>/<namespace>.json — otomatis ter-load.
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", short: "EN", dateLocale: enUS },
  { code: "id", label: "Bahasa Indonesia", short: "ID", dateLocale: idLocale },
  { code: "ms", label: "Bahasa Melayu", short: "MS", dateLocale: msLocale },
  { code: "hi", label: "हिन्दी", short: "HI", dateLocale: hiLocale },
];

export const DEFAULT_LANGUAGE = "en";

// Kunci yang sama dengan landing page (localStorage & cookie bernama sw_lang).
export const LANGUAGE_STORAGE_KEY = "sw_lang";

// Kode lama/alternatif dari browser → kode yang kita pakai.
const LANGUAGE_ALIASES = { in: "id", zsm: "ms" };

const resources = {};
const context = require.context("./locales", true, /\.json$/);
context.keys().forEach((key) => {
  const [, lng, ns] = key.match(/\.\/([^/]+)\/([^/]+)\.json$/);
  resources[lng] = resources[lng] || {};
  resources[lng][ns] = context(key);
});

export const normalizeLanguage = (code) => {
  const base = String(code || "")
    .toLowerCase()
    .split(/[-_;]/)[0];
  return LANGUAGE_ALIASES[base] || base;
};

export const isSupportedLanguage = (code) =>
  SUPPORTED_LANGUAGES.some((lang) => lang.code === code);

const readStorage = () => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const writeStorage = (code) => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch (e) {
    // Mode privat / storage diblokir: bahasa tetap berlaku untuk sesi ini.
  }
};

const readCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)sw_lang=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

// Cookie di domain induk (.sewoork.com) agar pilihan bahasa berlaku di
// sewoork.com dan app.sewoork.com sekaligus.
export const writeLanguageCookie = (code) => {
  const host = window.location.hostname;
  const domain = /(^|\.)sewoork\.com$/.test(host) ? "; domain=.sewoork.com" : "";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `sw_lang=${encodeURIComponent(code)}; path=/; max-age=31536000; SameSite=Lax${domain}${secure}`;
};

// Pilihan eksplisit pengunjung: ?lang= (dari landing page) → pilihan tersimpan → cookie sw_lang.
export const getExplicitLanguage = () => {
  const fromUrl = normalizeLanguage(
    new URLSearchParams(window.location.search).get("lang"),
  );
  if (isSupportedLanguage(fromUrl)) {
    writeStorage(fromUrl);
    return fromUrl;
  }

  const saved = normalizeLanguage(readStorage());
  if (isSupportedLanguage(saved)) return saved;

  const cookie = normalizeLanguage(readCookie());
  if (isSupportedLanguage(cookie)) return cookie;

  return null;
};

const detectBrowserLanguage = () => {
  const list = navigator.languages || [navigator.language || ""];
  for (const item of list) {
    const code = normalizeLanguage(item);
    if (isSupportedLanguage(code)) return code;
  }
  return null;
};

// Urutan: pilihan eksplisit → bahasa browser → default.
// Preferensi akun di server (users.language) diterapkan setelah profil dimuat — lihat LanguageService.
export const detectInitialLanguage = () =>
  getExplicitLanguage() || detectBrowserLanguage() || DEFAULT_LANGUAGE;

export const getDateLocale = () =>
  SUPPORTED_LANGUAGES.find((lang) => lang.code === i18n.language)?.dateLocale ||
  enUS;

const applyDocumentLanguage = (code) => {
  document.documentElement.lang = code;
  setDefaultOptions({ locale: getDateLocale() });
};

i18n.use(initReactI18next).init({
  resources,
  lng: detectInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES.map((lang) => lang.code),
  defaultNS: "common",
  ns: Object.keys(resources[DEFAULT_LANGUAGE] || { common: {} }),
  interpolation: { escapeValue: false },
  returnNull: false,
});

applyDocumentLanguage(i18n.language);
i18n.on("languageChanged", applyDocumentLanguage);

export { writeStorage as saveLanguagePreference, readStorage as readLanguagePreference };

// Terjemahan di luar hook (dipakai halaman hasil codemod: tr("users:new_user")).
// Halaman di-remount saat bahasa berganti (lihat LayoutMain & LanguageKeyed),
// sehingga teks tr() ikut diperbarui tanpa useTranslation di setiap komponen.
export const tr = (key, options) => i18n.t(key, options);

export default i18n;
